
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { TokenUsageStats } from '@/types/dashboard';

interface TokenUsageChartProps {
  tokenUsage: TokenUsageStats[];
  loading: boolean;
}

export const TokenUsageChart: React.FC<TokenUsageChartProps> = ({ tokenUsage, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Token-forbruk over tid</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
        </CardContent>
      </Card>
    );
  }

  // Grupper token-forbruk per time for de siste 24 timene
  const hourlyUsage = React.useMemo(() => {
    const now = new Date();
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const hourlyData: { [hour: string]: { total: number, prompt: number, completion: number } } = {};
    
    // Initialiser alle timer med 0
    for (let i = 0; i < 24; i++) {
      const hour = new Date(last24Hours.getTime() + i * 60 * 60 * 1000);
      const hourKey = hour.toISOString().slice(0, 13); // YYYY-MM-DDTHH
      hourlyData[hourKey] = { total: 0, prompt: 0, completion: 0 };
    }
    
    // Legg til faktisk data
    tokenUsage.forEach(usage => {
      const usageDate = new Date(usage.created_at);
      if (usageDate >= last24Hours) {
        const hourKey = usageDate.toISOString().slice(0, 13);
        if (hourlyData[hourKey]) {
          hourlyData[hourKey].total += usage.total_tokens;
          hourlyData[hourKey].prompt += usage.prompt_tokens;
          hourlyData[hourKey].completion += usage.completion_tokens;
        }
      }
    });
    
    return Object.entries(hourlyData)
      .map(([hour, data]) => ({
        time: new Date(hour).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }),
        total: data.total,
        prompt: data.prompt,
        completion: data.completion,
      }))
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [tokenUsage]);

  const totalTokensToday = tokenUsage
    .filter(usage => {
      const today = new Date().toDateString();
      return new Date(usage.created_at).toDateString() === today;
    })
    .reduce((sum, usage) => sum + usage.total_tokens, 0);

  const estimatedCost = tokenUsage
    .filter(usage => {
      const today = new Date().toDateString();
      return new Date(usage.created_at).toDateString() === today;
    })
    .reduce((sum, usage) => sum + (usage.cost_estimate || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Tokens i dag</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTokensToday.toLocaleString()}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Estimert kostnad</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${estimatedCost.toFixed(4)}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Aktive brukere</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(tokenUsage.filter(u => u.user_id).map(u => u.user_id)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Token-forbruk over tid</CardTitle>
          <CardDescription>Siste 24 timer (per time)</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={hourlyUsage}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Area 
                type="monotone" 
                dataKey="total" 
                stackId="1" 
                stroke="#8884d8" 
                fill="#8884d8" 
                name="Totale tokens"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};
